import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { FormlyModule } from '@ngx-formly/core';
import { ClipboardService } from 'ngx-clipboard';
import { TooltipModule } from 'primeng/tooltip';
import * as dot from 'ts-dot-prop';
import { I18nService } from '../../../@core/services/i18n/i18n.service';
import { NotificationService } from '../../../@core/services/notification/notification.service';
import { Product } from '../../../generated/generated-types';
import { getGenericBaseModelDropDownInstance } from '../../../shared/components/genericBaseModelDropDown';
import { ProductEditWidgetComponent } from '../../products/components/product-edit-widget/product-edit-widget.component';
import { isProductPropertyInherited } from '../../products/services/product.service';
import { IconDefinition, SimpleTitleWrapperComponent } from './simple-title-wrapper.component';

@Component({
    selector: 'formly-simple-title-wrapper-product',
    templateUrl: './simple-title-wrapper.component.html',
    styleUrls: ['./simple-title-wrapper.component.scss'],
    standalone: true,
    imports: [
        FormlyModule,
        CommonModule,
        TooltipModule,
    ]
})
export class SimpleTitleWrapperProductComponent extends SimpleTitleWrapperComponent {

    constructor(
        clipboardService: ClipboardService,
        notificationService: NotificationService,
        private i18nService: I18nService
    ) {
        super(clipboardService, notificationService);
    }

    // === ICON CONFIGURATION ===

    // Define which model properties affect our icons (for efficient change detection)
    protected override extractIconRelevantData(model: any): Record<string, any> {
        const product = model as Product;
        return {
            variantParentId: product?.variantParentId,
            nonInheritedProperties: product?.nonInheritedProperties
        };
    }

    /**
     * Determines the correct field key for storing in nonInheritedProperties.
     * For object fields, checks if a corresponding ID field exists and returns that instead.
     * 
     * Context: The backend automatically stores ID field names (e.g., 'primaryProductGroupId') in 
     * nonInheritedProperties when properties change, but the frontend UI uses object field names 
     * (e.g., 'primaryProductGroup') for dropdowns and form controls. This function bridges that gap.
     */
    private getStorageFieldKey(fieldKey: string): string {
        const model = this.modelSignal();
        if (!model) {
            return fieldKey;
        }

        if (!fieldKey) {
            return fieldKey;
        }

        // Check if this field key already ends with 'Id' - if so, return as-is
        if (fieldKey.endsWith('Id')) {
            return fieldKey;
        }

        // Check if a corresponding ID field exists on the model
        const idFieldKey = fieldKey + 'Id';
        if (model.hasOwnProperty(idFieldKey) || (model as any)[idFieldKey] !== undefined) {
            return idFieldKey;
        }

        // Return the original field key if no ID field is found
        return fieldKey;
    }

    // Define pre-icons (left side) - static definitions with dynamic evaluation
    protected override getPreIconDefinitions(): IconDefinition[] {
        return [
            {
                iconClass: 'fa fa-link', // Fallback, will be overridden by dynamicIconClass
                tooltip: 'Link / Unlink Value', // Fallback, will be overridden by dynamicTooltip
                clickHandler: (icon, event) => {
                    const model = this.modelSignal();
                    const isInherited = isProductPropertyInherited(model as Product, this.key as string);
                    this.updateNonInheritedProperty(model as Product, this.key as string, !isInherited);
                },
                // Show icon only for variant products
                condition: (model: Product) => {

                    if (!model) {
                        return false;
                    }

                    if (model.variantParentId != null) {
                        return true;
                    }

                    return false;
                },
                // Dynamic icon class based on inheritance status
                dynamicIconClass: (model: Product) => {

                    if (!model)
                        return '';

                    if (!this.key)
                        return '';

                    const isInherited = isProductPropertyInherited(model, this.key as string);

                    if (isInherited) {
                        return 'fa fa-link text-primary'; // Inherited (linked to parent)
                    } else {
                        return 'fa fa-link-slash text-gray-600'; // Not inherited (independent)
                    }
                },
                // Dynamic tooltip based on inheritance status
                dynamicTooltip: (model: Product) => {
                    if (!model) return 'Link / Unlink Value';

                    if (!this.key) {
                        return '';
                    }

                    const isInherited = isProductPropertyInherited(model, this.key as string);

                    if (isInherited) {
                        return this.i18nService.translate(_('product.make-variant-property-non-inherited'));

                    } else {
                        return this.i18nService.translate(_('product.make-variant-property-inherited'));

                    }
                }
            }
        ];
    }

    /**
     * Check if the current field property is inherited from the variant parent
     */
    private async updateNonInheritedProperty(model: Product, property: string, shouldBeInherited: boolean) {
        // Get the correct field key for backend storage (e.g., 'primaryProductGroup' -> 'primaryProductGroupId')
        const storageFieldKey = this.getStorageFieldKey(property);

        if (shouldBeInherited) {
            // Remove the storage field key from nonInheritedProperties (making it inherited)
            model.nonInheritedProperties = model.nonInheritedProperties?.filter(p => p !== storageFieldKey) || [];
        } else {
            // Add the storage field key to nonInheritedProperties (making it non-inherited)
            if (!model.nonInheritedProperties?.includes(storageFieldKey)) {
                model.nonInheritedProperties = [...(model.nonInheritedProperties || []), storageFieldKey];
            }
        }

        // model.nonInheritedProperties = pi.nonInheritedProperties; //update local model

        var editWidget = this.formState.detailComponent as ProductEditWidgetComponent;

        if (!editWidget) {
            console.error("editWidget is not set, cannot update non inherited properties");
            return;
        }

        if (shouldBeInherited) {
            //get parent value right away and set it to the model

            if (!model.variantParent) {
                console.warn("variant parent is not set, cannot get parent value");
            } {

                let parentValue = dot.get(model.variantParent, property);

                if (parentValue !== undefined) {
                    //model[property] = parentValue;
                    // this.form.patchValue([model], { emitEvent: true });
                    this.formControl.setValue(parentValue);
                    dot.set(model, property, parentValue);
                    //dot.set(pi, property, parentValue);
                    this.modelSignal.set(model);
                } else {

                    //we try a kinda dirty check if we can get the parent value via ID

                    let parentValueAsId = dot.get(model.variantParent, property + 'Id');

                    if (parentValueAsId) {

                        let myDropDown = getGenericBaseModelDropDownInstance(this.field);
                        if (myDropDown) {

                            await myDropDown.loadItemByIdAndSetSelected(parentValueAsId);

                            let selectedItem = myDropDown.value();

                            if (selectedItem) {
                                //COPY PASTED FROM ABOVE, but we patch form too
                                this.form.patchValue([model], { emitEvent: true });
                                this.formControl.setValue(selectedItem);
                                dot.set(model, property, selectedItem);
                                // dot.set(pi, property, selectedItem);
                                this.modelSignal.set(model);
                            }
                        }
                    }
                }
            }
        }

        this.field.props.disabled = shouldBeInherited;

        // Refresh icons to reflect the updated inheritance status
        this.refreshIcons();

        //hopefully this is more performant than the service in constructor
        // const injectedService = this.injector.get(ProductService);

        // await injectedService.saveItem(pi);


        // do we want to save right away?
        // await editWidget.save(); //the user maybe made other changes too, if we use the service directly then setModel would be called again from graphql / apollo
    }

    /**
     * Get product link for navigation (if needed for future use)
     */
    // private getProductLink(product?: Product): string {
    //     if (!product) return '';

    //     const basePath = this.propsSignal()?.productLinkBasePath || '/products/';
    //     const id = product.id;

    //     return id ? `${basePath}${id}` : '';
    // }
}
